import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { HiSearch, HiStar, HiLocationMarker, HiClock, HiUserGroup } from 'react-icons/hi';
import { FaFacebookF, FaInstagram, FaThreads } from 'react-icons/fa6';
import { BsArrowRight, BsCalendarEvent } from 'react-icons/bs';
import { businessApi, eventApi } from '../services/api';
import { format, parseISO } from 'date-fns';

const VIRGINIA_MAP = 'https://customer-assets.emergentagent.com/job_mhc-staging/artifacts/eiwoomzi_ChatGPT%20Image%20Mar%206%2C%202026%2C%2001_44_25%20PM.png';
const COMMUNITY_IMAGE = 'https://images.unsplash.com/photo-1758274533219-cc0ef0af4f60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxjaGVlcmZ1bCUyMGRpdmVyc2UlMjBncm91cCUyMG9mJTIwZnJpZW5kcyUyMHRhbGtpbmclMjBvdXRkb29ycyUyMHBhcmt8ZW58MHx8fHwxNzcyODE3MTg0fDA&ixlib=rb-4.1.0&q=85';

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [featuredBusinesses, setFeaturedBusinesses] = useState([]);
  const [minorityBusinesses, setMinorityBusinesses] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const MINORITY_TAGS = ["Black-Owned", "Hispanic/Latino-Owned", "Asian-Owned", "Native American-Owned"];

  const loadData = async () => {
    try {
      const [featuredBiz, featuredEv, allEvents] = await Promise.all([
        businessApi.getFeatured(),
        eventApi.getFeatured(),
        eventApi.getAll()
      ]);
      
      const allFeatured = featuredBiz.data || [];
      
      // Split featured businesses: minority-tagged go to Spotlight, others to Featured
      const minority = allFeatured.filter(b => 
        b.ownership_tags?.some(tag => MINORITY_TAGS.includes(tag))
      );
      const nonMinority = allFeatured.filter(b => 
        !b.ownership_tags?.some(tag => MINORITY_TAGS.includes(tag))
      );
      
      setFeaturedBusinesses(nonMinority.slice(0, 6));
      setMinorityBusinesses(minority.slice(0, 6));
      setFeaturedEvents(featuredEv.data.slice(0, 4));
      setUpcomingEvents(allEvents.data.slice(0, 6));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/businesses?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const formatEventDate = (dateStr) => {
    try {
      const date = parseISO(dateStr);
      return {
        month: format(date, 'MMM'),
        day: format(date, 'd'),
        full: format(date, 'EEEE, MMMM d, yyyy')
      };
    } catch {
      return { month: 'TBD', day: '--', full: dateStr };
    }
  };

  return (
    <div className="animate-fade-in" data-testid="homepage">
      <Helmet>
        <title>Explore MHC | Local Businesses & Events in Martinsville & Henry County, VA</title>
        <meta name="description" content="Discover local businesses, restaurants, shops, and community events in Martinsville and Henry County, Virginia. Your guide to everything happening in MHC." />
        <link rel="canonical" href="https://exploremhc.com/" />
        <meta property="og:title" content="Explore MHC | Local Businesses & Events in Martinsville & Henry County, VA" />
        <meta property="og:description" content="Discover local businesses, restaurants, shops, and community events in Martinsville and Henry County, Virginia." />
        <meta property="og:url" content="https://exploremhc.com/" />
        <meta property="og:image" content="https://images.unsplash.com/photo-1758274533219-cc0ef0af4f60?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200&h=630&fit=crop" />
        <meta property="og:type" content="website" />
      </Helmet>
      <section className="relative min-h-[85vh] flex items-center overflow-hidden bg-gradient-to-br from-white via-blue-50/30 to-white">
        {/* Virginia Map Background - hidden on mobile to avoid text overlap */}
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[65%] pointer-events-none hidden md:block" style={{ opacity: 0.4 }}>
          <img 
            src={VIRGINIA_MAP} 
            alt="Virginia Map" 
            className="w-full h-auto"
          />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">
              <HiLocationMarker className="w-4 h-4 text-red-500" />
              Martinsville & Henry County, VA
            </span>
            <h1 className="text-5xl md:text-7xl font-bold text-neutral-900 mb-6 leading-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>
              Explore Martinsville-Henry County VA <span className="text-primary">Businesses, Services & Events</span>
            </h1>
            <p className="text-xl text-neutral-600 mb-10 leading-relaxed max-w-2xl">
              Explore MHC is your guide to businesses, restaurants, services, and events in Martinsville-Henry County VA. Our local directory helps residents and visitors discover trusted companies, shops, dining, and things to do throughout Martinsville and Henry County, Virginia.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="search-bar-container mb-8">
              <input
                type="text"
                placeholder="Search businesses, restaurants, services..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                className="search-bar"
                data-testid="hero-search-input"
              />
              <button type="submit" className="search-bar-button" data-testid="hero-search-button">
                <HiSearch className="w-5 h-5" />
              </button>
            </form>

            {/* Quick Links */}
            <div className="flex flex-wrap gap-3">
              <Link to="/businesses?category=Restaurants%20%26%20Food" className="px-4 py-2 bg-white rounded-full text-sm font-medium text-neutral-700 border border-neutral-200 hover:border-primary hover:text-primary transition-colors">
                Restaurants
              </Link>
              <Link to="/businesses?category=Retail%20%26%20Shopping" className="px-4 py-2 bg-white rounded-full text-sm font-medium text-neutral-700 border border-neutral-200 hover:border-primary hover:text-primary transition-colors">
                Shopping
              </Link>
              <Link to="/events" className="px-4 py-2 bg-white rounded-full text-sm font-medium text-neutral-700 border border-neutral-200 hover:border-primary hover:text-primary transition-colors">
                Events
              </Link>
              <Link to="/minority-owned" className="px-4 py-2 bg-purple-100 rounded-full text-sm font-medium text-purple-700 border border-purple-200 hover:bg-purple-200 transition-colors">
                Minority-Owned
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Businesses Section */}
      <section className="section bg-white" data-testid="featured-businesses-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Featured Businesses
              </h2>
              <p className="text-neutral-600">Highlighted Martinsville VA businesses and Henry County VA services</p>
            </div>
            <Link to="/businesses" className="flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
              View All <BsArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : featuredBusinesses.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredBusinesses.map((business) => (
                <Link
                  key={business.id}
                  to={`/businesses/${business.slug || business.id}`}
                  className={`business-card hover-lift ${business.is_featured ? 'featured' : ''}`}
                  data-testid={`featured-business-${business.id}`}
                >
                  <div className="business-card-image flex items-center justify-center text-neutral-900 text-4xl font-bold">
                    {business.photos?.[0] ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${business.photos[0]}`} alt={business.business_name} className="w-full h-full object-contain" />
                    ) : (
                      business.business_name.charAt(0)
                    )}
                  </div>
                  <div className="business-card-content">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h3 className="font-bold text-lg text-neutral-900">{business.business_name}</h3>
                      {business.is_featured && (
                        <span className="badge badge-featured flex items-center gap-1">
                          <HiStar className="w-3 h-3" /> Featured
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-neutral-600 mb-3">{business.primary_category}</p>
                    <p className="text-sm text-neutral-500 line-clamp-2">{business.description}</p>
                    {business.ownership_tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {business.ownership_tags.slice(0, 2).map(tag => (
                          <span key={tag} className="badge badge-minority text-xs">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="cta-card" onClick={() => navigate('/register')}>
              <HiStar className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Feature Your Business Here</h3>
              <p className="text-neutral-600 mb-4">Get premium visibility and reach more customers</p>
              <span className="btn-primary inline-block">Get Started</span>
            </div>
          )}
        </div>
      </section>

      {/* Minority-Owned Spotlight */}
      <section className="section bg-gradient-to-br from-purple-50 to-white" data-testid="minority-spotlight-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
            <div>
              <span className="badge badge-minority mb-4">Paid Spotlight</span>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Minority-Owned Businesses
              </h2>
              <p className="text-neutral-600">Supporting diverse businesses in Martinsville and Henry County</p>
            </div>
            <Link to="/minority-owned" className="flex items-center gap-2 text-purple-700 font-semibold hover:gap-3 transition-all">
              View All <BsArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : minorityBusinesses.filter(b => b.is_featured).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {minorityBusinesses.filter(b => b.is_featured).slice(0, 6).map((business) => (
                <Link
                  key={business.id}
                  to={`/businesses/${business.slug || business.id}`}
                  className="business-card hover-lift featured"
                  data-testid={`minority-business-${business.id}`}
                >
                  <div className="business-card-image flex items-center justify-center text-neutral-900 text-4xl font-bold">
                    {business.photos?.[0] ? (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${business.photos[0]}`} alt={business.business_name} className="w-full h-full object-contain" />
                    ) : (
                      business.business_name.charAt(0)
                    )}
                  </div>
                  <div className="business-card-content">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-bold text-lg text-neutral-900">{business.business_name}</h3>
                      <span className="badge badge-featured flex items-center gap-1 flex-shrink-0">
                        <HiStar className="w-3 h-3" /> Featured
                      </span>
                    </div>
                    <p className="text-sm text-neutral-600 mb-3">{business.primary_category}</p>
                    <div className="flex flex-wrap gap-1">
                      {business.ownership_tags?.slice(0, 2).map(tag => (
                        <span key={tag} className="badge badge-minority text-xs">{tag}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="cta-card bg-gradient-to-br from-purple-50 to-purple-100 border-purple-300" onClick={() => navigate('/register')}>
              <HiUserGroup className="w-12 h-12 text-purple-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Feature Your Minority-Owned Business</h3>
              <p className="text-neutral-600 mb-4">Get spotlight visibility and connect with customers who support diverse businesses</p>
              <span className="btn-primary inline-block" style={{ backgroundColor: '#7C3AED' }}>Get Featured</span>
            </div>
          )}
        </div>
      </section>

      {/* Free Featured + Promo Section */}
      <section className="py-16 bg-gradient-to-r from-accent via-accent-light to-accent" data-testid="first-5-free-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-accent/10 to-secondary/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-primary/10 to-purple-500/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>

            <div className="relative z-10 max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/20 rounded-full mb-6">
                <span className="text-2xl">🎉</span>
                <span className="font-bold text-accent uppercase tracking-wider text-sm">Free Featured Placement For Everyone</span>
              </div>

              <h2 className="text-3xl md:text-5xl font-bold text-neutral-900 mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Every New Listing<br />
                <span className="text-accent">Starts Featured — On Us</span>
              </h2>

              <p className="text-lg text-neutral-600 mb-8 max-w-2xl mx-auto">
                Every new business listing gets their <strong>first month featured FREE</strong>, and every new event listing gets their <strong>first week featured FREE</strong> — automatically, on approval. No coupon, no catch.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto mb-8 text-left">
                <div className="flex items-start gap-3 p-4 bg-neutral-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center shrink-0">
                    <HiStar className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900">New Business?</div>
                    <div className="text-sm text-neutral-600">Your first month featured is on us.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-neutral-50 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                    <HiUserGroup className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-900">New Event?</div>
                    <div className="text-sm text-neutral-600">Your first week featured is on us.</div>
                  </div>
                </div>
              </div>

              <div className="inline-block bg-gradient-to-r from-accent/15 to-secondary/15 border-2 border-accent rounded-2xl px-6 py-4 mb-8">
                <div className="text-xs font-bold uppercase tracking-wider text-accent mb-1">Limited-Time Promo</div>
                <div className="text-lg font-bold text-neutral-900">$5 off every paid placement — now through December 31, 2026</div>
                <div className="text-xs text-neutral-600 mt-1">Renewals, extensions, and event boosts all qualify.</div>
              </div>

              <div>
                <Link to="/register" className="btn-accent text-lg px-10 py-4 inline-block">
                  Get Started Free
                </Link>
              </div>

              <p className="text-sm text-neutral-500 mt-4">
                Free featured placement begins once your listing is approved.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* We're now on Instagram & Threads! */}
      <section className="py-16 bg-[#FAF8F2]" data-testid="social-announce-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl border-2 border-neutral-200 shadow-lg overflow-hidden relative">
            {/* Decorative brush stroke top-left */}
            <div className="absolute top-6 left-6 w-20 h-20 opacity-20 pointer-events-none" aria-hidden="true">
              <svg viewBox="0 0 100 100" fill="none">
                <path d="M10,80 Q30,20 50,40 T90,30" stroke="#84CC16" strokeWidth="6" strokeLinecap="round" />
                <path d="M20,90 Q40,30 70,50" stroke="#84CC16" strokeWidth="5" strokeLinecap="round" />
              </svg>
            </div>

            <div className="grid lg:grid-cols-2 gap-0 items-stretch">
              {/* Left column — copy */}
              <div className="p-8 md:p-12 lg:p-14 relative z-10">
                <p className="text-2xl md:text-3xl font-bold text-neutral-700 mb-2" style={{ fontFamily: 'Caveat, "Brush Script MT", cursive', transform: 'rotate(-2deg)' }}>
                  We're now on
                </p>
                <h2 className="text-5xl md:text-6xl lg:text-7xl font-black leading-none mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  <span style={{ color: '#D6336C' }}>INSTAGRAM</span>
                </h2>
                <h2 className="text-5xl md:text-6xl lg:text-7xl font-black leading-none mb-8 text-primary" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  & THREADS!
                </h2>

                <p className="text-lg text-neutral-700 mb-8 max-w-md leading-relaxed">
                  Another way your business or event can <strong>gain exposure</strong>. Every listing on <strong>ExploreMHC.com</strong> gets shared across our social channels — free promotion built right in.
                </p>

                {/* Handle cards */}
                <div className="space-y-3 mb-8 max-w-md">
                  <a
                    href="https://instagram.com/exploremhc_"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-pink-50 to-orange-50 border-2 border-pink-100 hover:border-pink-400 hover:shadow-md transition-all group"
                    data-testid="home-instagram-link"
                  >
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center shadow-sm shrink-0">
                      <FaInstagram className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-lg text-neutral-900 group-hover:text-pink-600 transition-colors">@exploremhc_</div>
                      <div className="text-sm text-neutral-500">Follow on Instagram</div>
                    </div>
                    <span className="text-pink-500 font-bold text-2xl shrink-0">→</span>
                  </a>

                  <a
                    href="https://threads.com/@exploremhc_"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-2xl bg-neutral-50 border-2 border-neutral-200 hover:border-neutral-900 hover:shadow-md transition-all group"
                    data-testid="home-threads-link"
                  >
                    <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center shadow-sm shrink-0">
                      <FaThreads className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-lg text-neutral-900 group-hover:text-neutral-700 transition-colors">@exploremhc_</div>
                      <div className="text-sm text-neutral-500">Follow on Threads</div>
                    </div>
                    <span className="text-neutral-900 font-bold text-2xl shrink-0">→</span>
                  </a>
                </div>

                {/* Cross-platform footer note */}
                <div className="flex items-center gap-3 text-sm text-neutral-600">
                  <span>Follow us on</span>
                  <a href="https://www.facebook.com/exploremhc" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-[#1877F2] flex items-center justify-center hover:scale-110 transition-transform" aria-label="Facebook">
                    <FaFacebookF className="w-4 h-4 text-white" />
                  </a>
                  <a href="https://instagram.com/exploremhc_" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center hover:scale-110 transition-transform" aria-label="Instagram">
                    <FaInstagram className="w-4 h-4 text-white" />
                  </a>
                  <a href="https://threads.com/@exploremhc_" target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-black flex items-center justify-center hover:scale-110 transition-transform" aria-label="Threads">
                    <FaThreads className="w-4 h-4 text-white" />
                  </a>
                </div>
              </div>

              {/* Right column — bold CTA & value props */}
              <div className="bg-primary text-white p-8 md:p-12 lg:p-14 flex flex-col justify-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-accent/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" aria-hidden="true"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-lime-500/10 rounded-full translate-y-1/2 -translate-x-1/2 pointer-events-none" aria-hidden="true"></div>

                <div className="relative z-10">
                  <div className="inline-block bg-lime-400 text-neutral-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6">
                    Let's explore local
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-8 leading-tight" style={{ fontFamily: 'DM Serif Display, serif' }}>
                    Every listing gets <span className="text-lime-300">free</span> social media exposure
                  </h3>

                  <div className="space-y-5 mb-8">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <HiStar className="w-6 h-6 text-lime-300" />
                      </div>
                      <div>
                        <div className="font-bold text-lg">More Exposure</div>
                        <div className="text-sm text-white/80">Cross-shared on Facebook, Instagram &amp; Threads</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <HiUserGroup className="w-6 h-6 text-lime-300" />
                      </div>
                      <div>
                        <div className="font-bold text-lg">More Local Customers</div>
                        <div className="text-sm text-white/80">Connect with MHC residents who follow our pages</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                        <HiLocationMarker className="w-6 h-6 text-lime-300" />
                      </div>
                      <div>
                        <div className="font-bold text-lg">Stronger Community</div>
                        <div className="text-sm text-white/80">Help neighbors discover the businesses they love</div>
                      </div>
                    </div>
                  </div>

                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 bg-lime-400 hover:bg-lime-300 text-neutral-900 font-bold text-lg px-8 py-4 rounded-xl transition-colors shadow-lg"
                    data-testid="home-social-cta"
                  >
                    List your business or event FREE
                    <span className="text-2xl">→</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Events Section */}
      <section className="section bg-white" data-testid="featured-events-section">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Featured Events
              </h2>
              <p className="text-neutral-600">Things to do in Martinsville VA — don't miss these community events</p>
            </div>
            <Link to="/events" className="flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
              View Calendar <BsArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : featuredEvents.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredEvents.map((event) => {
                const dateInfo = formatEventDate(event.event_date);
                return (
                  <Link
                    key={event.id}
                    to={`/events/${event.slug || event.id}`}
                    className="event-card featured flex hover-lift"
                    data-testid={`featured-event-${event.id}`}
                  >
                    <div className="w-24 flex-shrink-0 bg-gradient-to-br from-primary to-primary-dark text-white flex flex-col items-center justify-center p-4">
                      <span className="text-sm font-bold uppercase">{dateInfo.month}</span>
                      <span className="text-3xl font-bold">{dateInfo.day}</span>
                    </div>
                    <div className="p-5 flex-1">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="font-bold text-lg text-neutral-900">{event.event_name}</h3>
                        <span className="badge badge-featured flex items-center gap-1">
                          <HiStar className="w-3 h-3" /> Featured
                        </span>
                      </div>
                      <p className="text-sm text-neutral-500 mb-3">{event.category}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-neutral-600">
                        <span className="flex items-center gap-1">
                          <HiClock className="w-4 h-4" /> {event.start_time} - {event.end_time}
                        </span>
                        <span className="flex items-center gap-1">
                          <HiLocationMarker className="w-4 h-4" /> {event.location}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="cta-card" onClick={() => navigate('/submit-event')}>
              <BsCalendarEvent className="w-12 h-12 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-bold text-neutral-900 mb-2">Feature Your Event Here</h3>
              <p className="text-neutral-600 mb-4">Reach more attendees with featured placement</p>
              <span className="btn-primary inline-block">Submit Event</span>
            </div>
          )}
        </div>
      </section>

      {/* Upcoming Events Preview */}
      {upcomingEvents.length > 0 && (
        <section className="section bg-neutral-50" data-testid="upcoming-events-section">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
                  Upcoming Events
                </h2>
                <p className="text-neutral-600">What's happening across Martinsville and Henry County</p>
              </div>
              <Link to="/events" className="flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all">
                View All <BsArrowRight className="w-5 h-5" />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingEvents.map((event) => {
                const dateInfo = formatEventDate(event.event_date);
                return (
                  <Link
                    key={event.id}
                    to={`/events/${event.slug || event.id}`}
                    className="event-card hover-lift"
                    data-testid={`upcoming-event-${event.id}`}
                  >
                    <div className="relative h-40 bg-gradient-to-br from-secondary to-secondary-light">
                      {event.image ? (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${event.image}`} alt={event.event_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-white text-5xl font-bold opacity-30">
                          {event.event_name.charAt(0)}
                        </div>
                      )}
                      <div className="event-card-date">
                        <span className="month">{dateInfo.month}</span>
                        <span className="day">{dateInfo.day}</span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-bold text-lg text-neutral-900 mb-1">{event.event_name}</h3>
                      <p className="text-sm text-neutral-500 mb-3">{event.category}</p>
                      <div className="flex items-center gap-1 text-sm text-neutral-600">
                        <HiLocationMarker className="w-4 h-4" /> {event.location}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Teaser */}
      <section className="section bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Get Featured
          </h2>
          <p className="text-neutral-600 mb-10 max-w-xl mx-auto">
            Affordable plans to put your business or event in front of the entire Martinsville-Henry County VA community.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-4xl mx-auto mb-10">
            <div className="p-5 rounded-xl border border-neutral-200 bg-neutral-50">
              <p className="text-xs font-medium text-neutral-500 uppercase mb-1">Business Weekly</p>
              <p className="text-2xl font-bold text-neutral-900">$10<span className="text-sm font-normal text-neutral-500">/wk</span></p>
            </div>
            <div className="p-5 rounded-xl border-2 border-accent bg-accent/5">
              <p className="text-xs font-medium text-accent uppercase mb-1">Business Monthly</p>
              <p className="text-2xl font-bold text-neutral-900">$30<span className="text-sm font-normal text-neutral-500">/mo</span></p>
            </div>
            <div className="p-5 rounded-xl border border-neutral-200 bg-neutral-50">
              <p className="text-xs font-medium text-neutral-500 uppercase mb-1">Event Weekly</p>
              <p className="text-2xl font-bold text-neutral-900">$10<span className="text-sm font-normal text-neutral-500">/wk</span></p>
            </div>
            <div className="p-5 rounded-xl border-2 border-accent bg-accent/5">
              <p className="text-xs font-medium text-accent uppercase mb-1">Until Event Date</p>
              <p className="text-2xl font-bold text-neutral-900">$25<span className="text-sm font-normal text-neutral-500"> flat</span></p>
            </div>
          </div>
          <Link to="/pricing" className="btn-accent" data-testid="homepage-pricing-link">
            View Full Pricing & Details
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-to-br from-primary to-primary-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src={COMMUNITY_IMAGE} 
            alt="Community" 
            className="w-32 h-32 rounded-full mx-auto mb-8 object-cover border-4 border-white/20"
          />
          <h2 className="text-3xl md:text-5xl font-bold mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Join Our Community
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto">
            Whether you're a local business owner or community member, there's a place for you at Explore MHC.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="btn-accent text-lg px-8 py-4">
              Create Free Account
            </Link>
            <Link to="/businesses" className="px-8 py-4 border-2 border-white/30 rounded-full text-lg font-semibold hover:bg-white/10 transition-colors">
              Browse Businesses
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
